"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import { useRef, type ReactNode, type RefObject } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

gsap.registerPlugin(ScrollTrigger);

const accent = "text-[#ff5c00]";

type ServiceCard = {
  title: string;
  paragraphs: string[];
};

const topRowServices: ServiceCard[] = [
  {
    title: "Website Design",
    paragraphs: [
      "No two businesses are the same, and neither are their websites. At Hi Ho, we design and build websites that are tailored to your goals, customers, and commercial realities.",
      "Hi Ho specialises in the design and development of smart intuitive Shopify websites, creating high-performance eCommerce experiences built to scale. For businesses that require flexibility, content-heavy structures, or bespoke functionality, we also deliver robust solutions using React for Shopify or development in WordPress/WooCommerce.",
      "Whether you're refreshing an existing site or building from the ground up, we design everything from streamlined brochure websites to complex online stores, always with your customers, sales environment, and budget in mind.",
    ],
  },
  {
    title: "WooCommerce to Shopify Migration",
    paragraphs: [
      "Many businesses start their eCommerce journey on WordPress/WooCommerce, but as they grow, they often look for a platform that is easier to manage, faster, and more scalable. At Hi Ho, we specialise in migrating WooCommerce websites to Shopify while preserving the look, structure, and brand identity of your existing site.",
      "This means you don't necessarily need to redevelop your entire website from scratch. Our team can transition your store's design, content, products, and customer data into Shopify while maintaining the user experience your customers already know.",
      "We carefully manage the migration process to ensure SEO structure, URLs, and key content are preserved wherever possible, minimising disruption to your search rankings and customer journey. The result is a familiar website experience powered by a more streamlined, secure, and scalable Shopify platform that's easier to maintain and built for long-term growth.",
    ],
  },
  {
    title: "eCommerce Development",
    paragraphs: [
      "eCommerce is what we do best. Hi Ho specialises in Shopify and React for Shopify, helping B2C, B2B, retail, and wholesale brands build reliable, conversion-focused online stores that perform around the clock.",
      "We also bring deep experience in WooCommerce for businesses that benefit from WordPress-based ecosystems. Our approach blends commercial strategy, sales psychology, advertising insight, and technical expertise to create eCommerce platforms that are easy to manage and built to sell.",
      "Every store we build is optimised for clear user journeys, efficient checkout flows, and strong conversion rates, so your website works as hard as you do.",
    ],
  },
];

function CaseStudyCard({
  title,
  children,
  services,
  id: articleId,
}: {
  title: string;
  children: ReactNode;
  /** All-caps service lines below the body (matches design spec). */
  services?: string[];
  id?: string;
}) {
  return (
    <article
      id={articleId}
      className="flex h-auto min-h-0 flex-col rounded-[1.75rem] bg-white p-6 shadow-sm sm:p-8 md:rounded-4xl md:p-10 lg:h-full lg:overflow-y-auto lg:overscroll-contain lg:rounded-[2.25rem]"
    >
      <p className={`text-2xl font-medium leading-none sm:text-3xl md:text-4xl ${accent}`} aria-hidden>
        *
      </p>
      <h3 className={`mt-4 text-xl font-semibold tracking-tight sm:mt-6 sm:text-2xl md:text-3xl ${accent}`}>
        {title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-[#231f20]/90 sm:mt-4 sm:text-base md:text-lg lg:min-h-0 lg:flex-1">
        {children}
      </p>
      {services && services.length > 0 ? (
        <ul className="mt-4 list-none space-y-1 text-[0.6875rem] font-medium uppercase leading-snug tracking-wide text-[#231f20]/75 sm:mt-5 sm:text-xs md:text-sm">
          {services.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function ImageCard({
  src,
  alt,
  priority,
  loading,
  slideRef,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  loading?: "eager" | "lazy";
  slideRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={slideRef}
      className="relative h-full min-h-0 w-full overflow-hidden rounded-[1.75rem] bg-[#f5f2f0] will-change-transform md:rounded-4xl lg:rounded-[2.25rem]"
    >
      <div className="relative h-full min-h-0 w-full">
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          loading={priority ? undefined : loading}
          className="object-cover"
          sizes="(max-width: 1023px) 100vw, 33vw"
        />
      </div>
    </div>
  );
}

export function ManageTeamSection({
  pageImageMap,
}: {
  pageImageMap?: SitePageImageMap;
}) {
  const grid1 = resolvePageImage("website-design-grid-1", pageImageMap);
  const grid2 = resolvePageImage("website-design-grid-2", pageImageMap);
  const grid3 = resolvePageImage("website-design-grid-3", pageImageMap);

  const scope = useRef<HTMLElement | null>(null);
  const topRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const bottomRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const thirdRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const topSlideRef = useRef<HTMLDivElement | null>(null);
  const bottomSlideRef = useRef<HTMLDivElement | null>(null);
  const thirdSlideRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const topRowTrigger = topRowTriggerRef.current;
      const bottomRowTrigger = bottomRowTriggerRef.current;
      const thirdRowTrigger = thirdRowTriggerRef.current;
      const topSlide = topSlideRef.current;
      const bottomSlide = bottomSlideRef.current;
      const thirdSlide = thirdSlideRef.current;
      if (
        !topRowTrigger ||
        !bottomRowTrigger ||
        !thirdRowTrigger ||
        !topSlide ||
        !bottomSlide ||
        !thirdSlide
      ) {
        return;
      }

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
        },
        (context) => {
          const { reduceMotion, isDesktop } = context?.conditions as {
            reduceMotion: boolean;
            isDesktop: boolean;
          };

          const rowScrollTrigger = (trigger: HTMLElement) => ({
            trigger,
            start: "top 88%",
            end: "top 60%",
            scrub: 1.15,
            invalidateOnRefresh: true,
          });

          if (!isDesktop || reduceMotion) {
            gsap.set(topSlide, { xPercent: 0 });
            gsap.set(bottomSlide, { xPercent: 0 });
            gsap.set(thirdSlide, { xPercent: 0 });
            return;
          }

          gsap.fromTo(
            topSlide,
            { xPercent: 109 },
            {
              xPercent: 0,
              ease: "none",
              scrollTrigger: rowScrollTrigger(topRowTrigger),
            },
          );
          gsap.fromTo(
            bottomSlide,
            { xPercent: -109 },
            {
              xPercent: 0,
              ease: "none",
              scrollTrigger: rowScrollTrigger(bottomRowTrigger),
            },
          );
          gsap.fromTo(
            thirdSlide,
            { xPercent: 109 },
            {
              xPercent: 0,
              ease: "none",
              scrollTrigger: rowScrollTrigger(thirdRowTrigger),
            },
          );
        },
      );

      return () => {
        mm.revert();
      };
    },
    { scope, dependencies: [] },
  );

  return (
    <section
      ref={scope}
      className="relative bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:pt-20 md:pb-28"
      aria-labelledby="website-design-manage-team-heading"
    >
      <div className="site-content">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[650px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              Website design and development
            </p>
            <h2
              id="website-design-manage-team-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              and the journey starts here
              <span className={accent}> on your website</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p
              className={`text-4xl font-medium leading-none md:text-5xl ${accent}`}
              aria-hidden
            >
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              Great websites design start with a partnership:
              your team who knows your
              business, and our skilled digital crew
              who knows how poeple interact with
              websites. We collaborate closely, keep
              things clear, fix today’s headaches, and
              build scalable, future-ready webesites,
              no jargon, no drama, no friction, just
              growth.
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-10 md:mt-20 md:gap-12 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-10 lg:overflow-visible lg:grid-rows-[auto_auto_auto] xl:gap-x-10 xl:gap-y-10">
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={topRowTriggerRef}
              className="aspect-9/17 w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-1 lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={topSlideRef}
                priority
                src={grid1.src}
                alt={grid1.alt}
              />
            </div>
            <div className="w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-1 lg:aspect-9/17 lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <CaseStudyCard
                id="website-design-design-card"
                title="Design"
                services={[
                  "Brand creation",
                  "Web design",
                  "UI design",
                  "UX design",
                  "Copywriting",
                  "Tone of voice",
                  "Image creation",
                ]}
              >
                We design websites that balance creativity with strategy, building digital experiences that
                capture attention, guide users intuitively, and drive action. Every site is shaped around your
                goals, your audience, and real user behaviour, removing friction, building trust, and turning
                interest into meaningful engagement and confident conversion.
              </CaseStudyCard>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={bottomRowTriggerRef}
              className="aspect-9/17 w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-2 lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={bottomSlideRef}
                loading="eager"
                src={grid2.src}
                alt={grid2.alt}
              />
            </div>
            <div className="w-full shrink-0 lg:relative lg:z-30 lg:col-start-1 lg:row-start-2 lg:aspect-9/17 lg:min-h-0 lg:w-auto lg:max-w-none lg:translate-x-[14%] xl:translate-x-0">
              <CaseStudyCard
                id="website-design-development-card"
                title="Development"
                services={[
                  "Shopify development",
                  "WordPress & WooCommerce development",
                  "React development",
                  "Custom Shopify and React apps",
                  "B2B / wholesale",
                ]}
              >
                We design and develop websites that balance creativity with strategy, building digital
                experiences that capture attention and drive action. We create bespoke solutions in Shopify,
                WordPress, WooCommerce, and React, each tailored to your goals, your audience, and real user
                behaviour, removing friction, building trust, and turning visitors into confident customers.
              </CaseStudyCard>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={thirdRowTriggerRef}
              className="aspect-9/17 w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-3 lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={thirdSlideRef}
                loading="eager"
                src={grid3.src}
                alt={grid3.alt}
              />
            </div>
            <div className="w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-3 lg:aspect-9/17 lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <CaseStudyCard
                id="website-design-solutions-card"
                title="Solutions"
                services={[
                  "Strategy development",
                  "Shopify design and development",
                  "Email marketing",
                  "AI & SEO campaigns",
                  "Google Ads campaigns",
                ]}
              >
                Beyond design and development, we partner with clients long term, offering SLAs, strategic
                guidance, and ongoing marketing campaigns and support. With deep ecommerce expertise, we help
                shape smarter decisions, uncover opportunities, and provide tailored support that evolves with
                your business, your goals, and the challenges that come with growth.
              </CaseStudyCard>
            </div>
          </div>
        </div>

        <div
          id="website-design-services"
          className="mt-16 scroll-mt-24 border-t border-[#231f20]/40 pt-10 md:mt-20 md:scroll-mt-28 md:pt-14"
          aria-label="Website design services"
        >
          <div className="grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-20 lg:gap-y-20">
            {topRowServices.map((service) => (
              <article key={service.title}>
                <p className={`text-3xl leading-none md:text-4xl ${accent}`} aria-hidden>
                  *
                </p>
                <h3 className={`mt-5 text-3xl font-medium leading-[1.05] ${accent}`}>
                  {service.title}
                </h3>
                <div className="mt-5 space-y-4 text-base leading-relaxed text-[#231f20]/70 md:text-lg">
                  {service.paragraphs.map((para, i) => (
                    <p key={`${service.title}-${i}`}>{para}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
